import { SymbolPicker } from './SymbolPicker';
import { BackingControls } from './BackingControls';
import { pointPresentation } from '../../../symbols/scene';
import { SymbolColourControl } from './SymbolColourControl';
import { isImageGraphic } from '../../../symbols/model';
import type { SymbolTarget } from '../../../symbols/targets';
import type { GeometryFamily } from '../../../styles/geometry';
import type { GeometrySymbolPatch, LineDashPreset, VectorSymbols } from '../../../styles/model';
import { ColourControl } from './ColourControl';
import { NumericControl } from './NumericControl';

interface SymbolOverrideEditorProps {
  readonly target: SymbolTarget;
  readonly families: readonly GeometryFamily[];
  readonly id: string;
  readonly onChange: (patch: GeometrySymbolPatch) => void;
  readonly onReset: () => void;
  readonly symbols: VectorSymbols;
}

export function SymbolOverrideEditor({
  families,
  target,
  id,
  onChange,
  onReset,
  symbols,
}: SymbolOverrideEditorProps) {
  return (
    <div className="symbol-override-editor">
      {families.includes('point') ? (
        <fieldset>
          <legend>Point override</legend>
          <SymbolPicker
            target={target}
            shapes={['circle', 'square', 'triangle']}
            graphic={symbols.point.shape}
            presentation={pointPresentation(symbols.point)}
            colour={symbols.point.fillColor}
            {...(symbols.point.tint === undefined ? {} : { tint: symbols.point.tint })}
            onChange={(shape, anchor) =>
              onChange({ geometry: 'point', values: { shape, ...(anchor ? { anchor } : {}) } })
            }
          />
          <SymbolColourControl
            id={`${id}-point-fill`}
            graphic={symbols.point.shape}
            colour={symbols.point.fillColor}
            {...(symbols.point.tint === undefined ? {} : { tint: symbols.point.tint })}
            onColour={(fillColor) => onChange({ geometry: 'point', values: { fillColor } })}
            onTint={(tint) => onChange({ geometry: 'point', values: { tint } })}
          />
          <NumericControl
            id={`${id}-point-size`}
            label="Size"
            max={isImageGraphic(symbols.point.shape) ? 80 : 40}
            min={isImageGraphic(symbols.point.shape) ? 2 : 1}
            onChange={(size) =>
              onChange({
                geometry: 'point',
                values: { radius: isImageGraphic(symbols.point.shape) ? size / 2 : size },
              })
            }
            step={0.5}
            suffix="px"
            value={symbols.point.radius * (isImageGraphic(symbols.point.shape) ? 2 : 1)}
          />
          <NumericControl
            id={`${id}-point-opacity`}
            label="Opacity"
            max={100}
            min={0}
            onChange={(opacity) =>
              onChange({ geometry: 'point', values: { opacity: opacity / 100 } })
            }
            step={1}
            suffix="%"
            value={Math.round(symbols.point.opacity * 100)}
          />
          {!isImageGraphic(symbols.point.shape) ? (
            <>
              <ColourControl
                id={`${id}-point-outline`}
                label="Outline"
                onChange={(outlineColor) =>
                  onChange({ geometry: 'point', values: { outlineColor } })
                }
                value={symbols.point.outlineColor}
              />
              <NumericControl
                id={`${id}-point-outline-width`}
                label="Outline width"
                max={12}
                min={0}
                onChange={(outlineWidth) =>
                  onChange({ geometry: 'point', values: { outlineWidth } })
                }
                step={0.5}
                suffix="px"
                value={symbols.point.outlineWidth}
              />
            </>
          ) : (
            <>
              <NumericControl
                id={`${id}-rotation`}
                label="Rotation"
                min={-180}
                max={180}
                step={1}
                suffix="°"
                value={symbols.point.rotationDegrees ?? 0}
                onChange={(rotationDegrees) =>
                  onChange({ geometry: 'point', values: { rotationDegrees } })
                }
              />
              <div className="style-field style-field-inline">
                <label htmlFor={`${id}-anchor`}>Anchor</label>
                <select
                  id={`${id}-anchor`}
                  value={symbols.point.anchor ?? 'center'}
                  onChange={(e) =>
                    onChange({
                      geometry: 'point',
                      values: { anchor: e.target.value as 'center' | 'bottom' },
                    })
                  }
                >
                  <option value="center">Centre</option>
                  <option value="bottom">Bottom centre</option>
                </select>
              </div>
            </>
          )}
          <BackingControls
            graphic={symbols.point.shape}
            colour={symbols.point.fillColor}
            tint={symbols.point.tint}
            size={symbols.point.radius * 2}
            backing={symbols.point.backing}
            onChange={(backing) => onChange({ geometry: 'point', values: { backing } })}
          />
        </fieldset>
      ) : null}

      {families.includes('line') ? (
        <fieldset>
          <legend>Line override</legend>
          <ColourControl
            id={`${id}-line-colour`}
            label="Colour"
            onChange={(color) => onChange({ geometry: 'line', values: { color } })}
            value={symbols.line.color}
          />
          <NumericControl
            id={`${id}-line-width`}
            label="Width"
            max={30}
            min={0.5}
            onChange={(width) => onChange({ geometry: 'line', values: { width } })}
            step={0.5}
            suffix="px"
            value={symbols.line.width}
          />
          <NumericControl
            id={`${id}-line-opacity`}
            label="Opacity"
            max={100}
            min={0}
            onChange={(opacity) =>
              onChange({ geometry: 'line', values: { opacity: opacity / 100 } })
            }
            step={1}
            suffix="%"
            value={Math.round(symbols.line.opacity * 100)}
          />
          <div className="style-field style-field-inline">
            <label htmlFor={`${id}-line-dash`}>Dash</label>
            <select
              id={`${id}-line-dash`}
              onChange={(event) =>
                onChange({
                  geometry: 'line',
                  values: { dash: event.target.value as LineDashPreset },
                })
              }
              value={symbols.line.dash}
            >
              <option value="solid">Solid</option>
              <option value="dashed">Dashed</option>
              <option value="shortDash">Short dash</option>
              <option value="dotted">Dotted</option>
              <option value="dashDot">Dash-dot</option>
            </select>
          </div>
          <NumericControl
            id={`${id}-line-offset`}
            label="Offset"
            max={30}
            min={-30}
            onChange={(offset) => onChange({ geometry: 'line', values: { offset } })}
            step={0.5}
            suffix="px"
            value={symbols.line.offset}
          />
        </fieldset>
      ) : null}

      {families.includes('polygon') ? (
        <fieldset>
          <legend>Polygon override</legend>
          <ColourControl
            id={`${id}-polygon-fill`}
            label="Fill"
            onChange={(fillColor) => onChange({ geometry: 'polygon', values: { fillColor } })}
            value={symbols.polygon.fillColor}
          />
          <NumericControl
            id={`${id}-polygon-opacity`}
            label="Fill opacity"
            max={100}
            min={0}
            onChange={(fillOpacity) =>
              onChange({ geometry: 'polygon', values: { fillOpacity: fillOpacity / 100 } })
            }
            step={1}
            suffix="%"
            value={Math.round(symbols.polygon.fillOpacity * 100)}
          />
          <ColourControl
            id={`${id}-polygon-outline`}
            label="Outline"
            onChange={(outlineColor) => onChange({ geometry: 'polygon', values: { outlineColor } })}
            value={symbols.polygon.outlineColor}
          />
          <NumericControl
            id={`${id}-polygon-outline-width`}
            label="Outline width"
            max={12}
            min={0}
            onChange={(outlineWidth) => onChange({ geometry: 'polygon', values: { outlineWidth } })}
            step={0.5}
            suffix="px"
            value={symbols.polygon.outlineWidth}
          />
        </fieldset>
      ) : null}

      <button className="secondary-button symbol-override-reset" onClick={onReset} type="button">
        Reset override
      </button>
    </div>
  );
}
